import React, { useState, useEffect } from 'react';

const CONNECTION_TYPES = [
  { value: 'pppoe', label: 'PPPoE', desc: 'ใช้ Username/Password จากผู้ให้บริการ', icon: '🔐' },
  { value: 'dhcp_client', label: 'DHCP Client', desc: 'รับ IP อัตโนมัติจาก ISP', icon: '🔄' },
  { value: 'static', label: 'Static IP', desc: 'IP คงที่จาก ISP', icon: '📌' },
  { value: 'pptp_client', label: 'PPTP Client', desc: 'เชื่อมต่อ VPN แบบ PPTP', icon: '🔗' },
  { value: 'l2tp_client', label: 'L2TP Client', desc: 'เชื่อมต่อ VPN แบบ L2TP/IPSec', icon: '🛡️' },
  { value: 'sstp_client', label: 'SSTP Client', desc: 'เชื่อมต่อ VPN ผ่าน HTTPS (พอร์ต 443)', icon: '🔒' },
  { value: 'ovpn_client', label: 'OpenVPN', desc: 'เชื่อมต่อ OpenVPN Client', icon: '🌐' },
];

const IFACE_OPTIONS = [
  'ether1','ether2','ether3','ether4','ether5',
  'sfp1','sfp2','sfp-sfpplus1','sfp-sfpplus2',
];

function defaultWan(index) {
  return {
    type: 'pppoe',
    wan_interface: IFACE_OPTIONS[index] || 'ether' + (index + 1),
    interface_name: '',
    username: '',
    password: '',
    ip_address: '',
    gateway: '',
    use_peer_dns: true,
    add_default_route: true,
    route_distance: String(index + 1),
    comment: 'WAN-' + (index + 1),
  };
}

function autoInterfaceName(type, index) {
  const map = {
    pppoe: 'pppoe-out',
    pptp_client: 'pptp-out',
    l2tp_client: 'l2tp-out',
    sstp_client: 'sstp-out',
    ovpn_client: 'ovpn-out',
  };
  return (map[type] || 'wan') + (index + 1);
}

const DEFAULTS = {
  wans: [defaultWan(0)],
  dns: '8.8.8.8,8.8.4.4',
  allow_remote_dns: true,
  lan_network: '192.168.88.0/24',
};

export default function StepInternet({ data, onChange, onNext, onBack, interfaceNames }) {
  const [form, setForm] = useState(data || DEFAULTS);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const save = (updated) => {
    setForm(updated);
    onChange(updated);
  };

  const updateGlobal = (key, value) => {
    save({ ...form, [key]: value });
  };

  const updateWan = (index, key, value) => {
    const wans = [...form.wans];
    wans[index] = { ...wans[index], [key]: value };
    if (key === 'type' && !wans[index].interface_name) {
      wans[index].interface_name = autoInterfaceName(value, index);
    }
    save({ ...form, wans });
  };

  const addWan = () => {
    const idx = form.wans.length;
    const ifaceList = interfaceNames && interfaceNames.length > 0 ? interfaceNames : IFACE_OPTIONS;
    const newWan = {
      type: 'pppoe',
      wan_interface: ifaceList[idx] || ifaceList[0] || 'ether1',
      interface_name: '',
      username: '',
      password: '',
      ip_address: '',
      gateway: '',
      use_peer_dns: true,
      add_default_route: true,
      route_distance: String(idx + 1),
      comment: 'WAN-' + (idx + 1),
    };
    newWan.interface_name = autoInterfaceName(newWan.type, idx);
    save({ ...form, wans: [...form.wans, newWan] });
  };

  const removeWan = (index) => {
    if (form.wans.length <= 1) return;
    save({ ...form, wans: form.wans.filter((_, i) => i !== index) });
  };

  const duplicateWan = (index) => {
    const copy = { ...form.wans[index] };
    const idx = form.wans.length;
    const ifaceList = interfaceNames && interfaceNames.length > 0 ? interfaceNames : IFACE_OPTIONS;
    copy.wan_interface = ifaceList[idx] || ifaceList[0] || 'ether1';
    copy.interface_name = autoInterfaceName(copy.type, idx);
    copy.comment = 'WAN-' + (idx + 1);
    copy.route_distance = String(idx + 1);
    save({ ...form, wans: [...form.wans, copy] });
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">🌐 ตั้งค่าการเชื่อมต่ออินเทอร์เน็ต (WAN)</h2>
        <p className="text-gray-500 dark:text-gray-400">เพิ่มการเชื่อมต่อ WAN หนึ่งหรือหลายเส้น แต่ละเส้นสามารถเลือกประเภทการเชื่อมต่อได้</p>
      </div>

      {/* WAN Cards */}
      <div className="space-y-4 mb-6">
        {form.wans.map((wan, index) => (
          <div key={index} className="p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold shadow-md">{index + 1}</span>
                <div>
                  <span className="font-bold text-gray-900 dark:text-white">{wan.comment || 'WAN-' + (index + 1)}</span>
                  <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full font-medium">
                    {CONNECTION_TYPES.find(t => t.value === wan.type)?.label || wan.type}
                  </span>
                </div>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => duplicateWan(index)} className="px-2.5 py-1 text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors font-medium" title="ทำซ้ำ">📋 ทำซ้ำ</button>
                <button onClick={() => removeWan(index)} className="btn-danger" disabled={form.wans.length <= 1}>ลบ</button>
              </div>
            </div>

            <div className="space-y-4">
              {/* Connection Type */}
              <div>
                <label className="label">ประเภทการเชื่อมต่อ</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {CONNECTION_TYPES.map(ct => (
                    <button
                      key={ct.value}
                      onClick={() => updateWan(index, 'type', ct.value)}
                      className={`p-2.5 rounded-xl border-2 text-left text-xs transition-all ${
                        wan.type === ct.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200 dark:ring-blue-700'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      <div className="text-lg mb-0.5">{ct.icon}</div>
                      <div className="font-bold">{ct.label}</div>
                      <div className="opacity-70 text-[10px] leading-tight mt-0.5">{ct.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* WAN Interface */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">พอร์ต WAN (Physical Interface)</label>
                  <select
                    value={wan.wan_interface}
                    onChange={e => updateWan(index, 'wan_interface', e.target.value)}
                    className="input-field"
                  >
                    {(interfaceNames && interfaceNames.length > 0 ? interfaceNames : IFACE_OPTIONS).map(iface => (
                      <option key={iface} value={iface}>{iface}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">ชื่อ Interface (Logical)</label>
                  <input type="text" value={wan.interface_name} onChange={e => updateWan(index, 'interface_name', e.target.value)} className="input-field" placeholder="อัตโนมัติ" />
                  <p className="text-[10px] text-gray-400 mt-1">เว้นว่างไว้ ระบบจะตั้งชื่ออัตโนมัติ</p>
                </div>
              </div>

              {/* PPPoE fields */}
              {wan.type === 'pppoe' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800/50">
                  <div>
                    <label className="label">🔑 Username</label>
                    <input type="text" value={wan.username} onChange={e => updateWan(index, 'username', e.target.value)} className="input-field" placeholder="ชื่อผู้ใช้จากผู้ให้บริการ" />
                  </div>
                  <div>
                    <label className="label">🔒 Password</label>
                    <input type="password" value={wan.password} onChange={e => updateWan(index, 'password', e.target.value)} className="input-field" placeholder="รหัสผ่านจากผู้ให้บริการ" />
                  </div>
                </div>
              )}

              {/* VPN fields */}
              {(wan.type === 'pptp_client' || wan.type === 'l2tp_client' || wan.type === 'sstp_client' || wan.type === 'ovpn_client') && (
                <div className="p-3 bg-purple-50 dark:bg-purple-900/10 rounded-lg border border-purple-100 dark:border-purple-800/50 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="label">🖥️ ที่อยู่เซิร์ฟเวอร์ (IP/Hostname)</label>
                      <input type="text" value={wan.username} onChange={e => updateWan(index, 'username', e.target.value)} className="input-field" placeholder="vpn.example.com" />
                    </div>
                    <div>
                      <label className="label">👤 Username</label>
                      <input type="text" value={wan.password} onChange={e => updateWan(index, 'password', e.target.value)} className="input-field" placeholder="ชื่อผู้ใช้ VPN" />
                    </div>
                  </div>
                  <div>
                    <label className="label">🔒 Password</label>
                    <input type="password" value={wan.ip_address} onChange={e => updateWan(index, 'ip_address', e.target.value)} className="input-field" placeholder="รหัสผ่าน VPN" />
                  </div>
                  {wan.type === 'l2tp_client' && (
                    <div>
                      <label className="label">🛡️ IPsec Pre-Shared Key</label>
                      <input type="password" value={wan.gateway} onChange={e => updateWan(index, 'gateway', e.target.value)} className="input-field" placeholder="คีย์ลับ IPSec" />
                    </div>
                  )}
                  {wan.type === 'ovpn_client' && (
                    <div>
                      <label className="label">📜 ชื่อใบรับรอง (Certificate)</label>
                      <input type="text" value={wan.gateway} onChange={e => updateWan(index, 'gateway', e.target.value)} className="input-field" placeholder="client-cert" />
                    </div>
                  )}
                </div>
              )}

              {/* Static IP fields */}
              {wan.type === 'static' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-100 dark:border-green-800/50">
                  <div>
                    <label className="label">📌 IP Address (พร้อม CIDR)</label>
                    <input type="text" value={wan.ip_address} onChange={e => updateWan(index, 'ip_address', e.target.value)} className="input-field" placeholder="203.0.113.2/29" />
                  </div>
                  <div>
                    <label className="label">🚪 Gateway</label>
                    <input type="text" value={wan.gateway} onChange={e => updateWan(index, 'gateway', e.target.value)} className="input-field" placeholder="203.0.113.1" />
                  </div>
                </div>
              )}

              {/* Route settings */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-center gap-2.5 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 cursor-pointer hover:border-blue-200 dark:hover:border-blue-700 transition-colors">
                  <input type="checkbox" checked={wan.add_default_route} onChange={e => updateWan(index, 'add_default_route', e.target.checked)} className="w-4 h-4 text-blue-600 rounded" />
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">สร้าง Default Route</span>
                    <span className="block text-[10px] text-gray-400">ใช้ WAN นี้เป็นเส้นทางหลัก</span>
                  </div>
                </label>
                <label className="flex items-center gap-2.5 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 cursor-pointer hover:border-blue-200 dark:hover:border-blue-700 transition-colors">
                  <input type="checkbox" checked={wan.use_peer_dns} onChange={e => updateWan(index, 'use_peer_dns', e.target.checked)} className="w-4 h-4 text-blue-600 rounded" />
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">ใช้ DNS จาก ISP</span>
                    <span className="block text-[10px] text-gray-400">รับ DNS จากผู้ให้บริการ</span>
                  </div>
                </label>
                <div>
                  <label className="label">ระยะ Route (Distance)</label>
                  <input type="number" min="1" max="255" value={wan.route_distance} onChange={e => updateWan(index, 'route_distance', e.target.value)} className="input-field" />
                  <p className="text-[10px] text-gray-400 mt-1">ค่าน้อย = ลำดับความสำคัญสูง</p>
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="label">💬 หมายเหตุ</label>
                <input type="text" value={wan.comment} onChange={e => updateWan(index, 'comment', e.target.value)} className="input-field" placeholder="WAN-1 (เส้นหลัก)" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add WAN button */}
      <button onClick={addWan} className="mb-6 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500 dark:hover:border-blue-400 dark:hover:text-blue-400 transition-colors w-full font-medium text-sm">
        + เพิ่มการเชื่อมต่อ WAN
      </button>

      {/* Global Settings */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-6">
        <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          ตั้งค่าทั่วไป
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">🔍 DNS Server (คั่นด้วยจุลภาค)</label>
            <input type="text" value={form.dns} onChange={e => updateGlobal('dns', e.target.value)} className="input-field" placeholder="8.8.8.8,8.8.4.4,1.1.1.1" />
          </div>
          <div>
            <label className="label">🏠 เครือข่าย LAN (สำหรับ Firewall)</label>
            <input type="text" value={form.lan_network} onChange={e => updateGlobal('lan_network', e.target.value)} className="input-field" placeholder="192.168.88.0/24" />
          </div>
        </div>
        <label className="flex items-center gap-2 mt-2 cursor-pointer">
          <input type="checkbox" checked={form.allow_remote_dns} onChange={e => updateGlobal('allow_remote_dns', e.target.checked)} className="w-4 h-4 text-blue-600 rounded" />
          <span className="text-sm text-gray-600 dark:text-gray-400">อนุญาตให้เครื่องในเครือข่ายใช้ DNS ของ Router</span>
        </label>
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={onNext} className="btn-primary">ถัดไป ({form.wans.length} WAN)</button>
      </div>
    </div>
  );
}
